import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SHARED_WITH_YOU_CONSTANTS } from '../../constants/sharedWithMe.constants';
import { useSocket } from '../../context/WebsocketContext';
import { MARK_SHARED_STORY_READ } from '../../graphql/resolvers/sharedWithMeMutations/markSharedStoryRead.gql';
import { GET_SHARED_WITH_ME } from '../../graphql/resolvers/sharedWithMeQueries/getSharedWithMe.gql';
import type { SharedItemInterface } from '../../interfaces/sharedWithMe.interface';
import SharedItemRow from './SharedItemRow';
import './shared-with-you.scss';

interface SharedWithYouProps {
  position: 'top' | 'bottom';
  onMove: () => void;
}

export default function SharedWithYou({
  position,
  onMove,
}: SharedWithYouProps) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(GET_SHARED_WITH_ME, {
    variables: { limit: 4 },
    pollInterval: 120_000,
    fetchPolicy: 'cache-and-network',
  });

  const [markRead] = useMutation(MARK_SHARED_STORY_READ);
  const { onSharedStoryNotification } = useSocket();

  useEffect(() => {
    const unsubscribe = onSharedStoryNotification(() => {
      refetch();
    });
    return unsubscribe;
  }, [onSharedStoryNotification, refetch]);

  const items: SharedItemInterface[] = data?.sharedWithMe?.items || [];
  const unreadCount: number = data?.sharedWithMe?.unreadCount || 0;
  const moveLabel = position === 'top' ? 'Move down \u2193' : 'Move up \u2191';

  const handleRead = async (storyId: string) => {
    try {
      await markRead({ variables: { storyId } });
      refetch();
    } catch (err) {
      console.error('Failed to mark story as read:', err);
    }
  };

  return (
    <div className="SharedWithYou">
      <div className="SharedWithYou__Header">
        <div className="SharedWithYou__TitleRow">
          <h3 className="SharedWithYou__Title">
            {SHARED_WITH_YOU_CONSTANTS.TITLE}
          </h3>
          {unreadCount > 0 && (
            <span className="SharedWithYou__Badge">{unreadCount}</span>
          )}
        </div>
        <button
          type="button"
          className="SharedWithYou__MoveBtn"
          onClick={onMove}
        >
          {moveLabel}
        </button>
      </div>

      {loading && items.length === 0 ? (
        <SkeletonRows />
      ) : error ? (
        <div className="SharedWithYou__Empty">
          {SHARED_WITH_YOU_CONSTANTS.ERROR}
        </div>
      ) : items.length === 0 ? (
        <div className="SharedWithYou__Empty">
          {SHARED_WITH_YOU_CONSTANTS.EMPTY}
        </div>
      ) : (
        <>
          <div className="SharedWithYou__List">
            {items.map((item) => (
              <SharedItemRow
                key={`${item.storyId}-${item.sharedAt}`}
                item={item}
                onRead={handleRead}
              />
            ))}
          </div>
          <button
            type="button"
            className="SharedWithYou__SeeAll"
            onClick={() => {
              const path = window.location.pathname;
              const userMatch = path.match(/\/user\/([^/]+)/);
              const userPrefix = userMatch ? `/user/${userMatch[1]}` : '';
              navigate(`${userPrefix}/shared`);
            }}
          >
            {SHARED_WITH_YOU_CONSTANTS.SEE_ALL}
          </button>
        </>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="SharedWithYou__Skeleton__Row">
          <div className="SharedWithYou__Skeleton__Line SharedWithYou__Skeleton__Line--short" />
          <div className="SharedWithYou__Skeleton__Line SharedWithYou__Skeleton__Line--long" />
        </div>
      ))}
    </div>
  );
}
